import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { CheckCircle, Eye, AlertTriangle, FileDown } from "lucide-react";
import { toast } from "sonner";
import { getBills, bulkMarkPaid } from "@/lib/api";
import { exportCSV } from "@/lib/exportUtils";
import PaymentDialog from "@/components/PaymentDialog";
import FilterBar, { useSessionFilters } from "@/components/FilterBar";

const DUES_FILTER_DEFAULTS = {
  society: "all", tower: "all", dueStatus: "all",
  amountMin: "", amountMax: "", dueDateFrom: "", dueDateTo: "",
};

export default function DuesTracker() {
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [payBill, setPayBill] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkPayOpen, setBulkPayOpen] = useState(false);
  const [bulkPayDate, setBulkPayDate] = useState(new Date().toISOString().split("T")[0]);
  const [bulkPayMode, setBulkPayMode] = useState("Cash");
  const [filters, setFilters] = useSessionFilters("dues", DUES_FILTER_DEFAULTS);

  useEffect(() => { getBills().then(setBills).catch(() => {}); }, []);

  const unpaidBills = useMemo(() => {
    const now = new Date();
    return bills
      .filter((b) => !b.isPaid && b.status !== "paid")
      .map((b) => {
        const paid = b.totalPaid || 0;
        const remaining = Math.max(0, (b.netPayable || 0) - paid);
        const status = b.status || (paid > 0 ? "partial" : "unpaid");
        return { ...b, paidAmount: paid, remaining, displayStatus: status, daysOverdue: Math.max(0, Math.floor((now - new Date(b.dueDate)) / 86400000)), isOverdue: new Date(b.dueDate) < now };
      })
      .sort((a, b) => b.daysOverdue - a.daysOverdue);
  }, [bills]);

  const societies = useMemo(() => Array.from(new Set(unpaidBills.map((b) => b.society).filter(Boolean))).sort(), [unpaidBills]);
  const towers = useMemo(() => {
    const src = filters.society !== "all" ? unpaidBills.filter((b) => b.society === filters.society) : unpaidBills;
    return Array.from(new Set(src.map((b) => b.towerNo).filter(Boolean))).sort();
  }, [unpaidBills, filters.society]);

  const filterConfig = useMemo(() => [
    { key: "society", label: "Society", type: "select", options: [{ value: "all", label: "All Societies" }, ...societies.map((s) => ({ value: s, label: s }))] },
    { key: "tower", label: "Tower", type: "select", options: [{ value: "all", label: "All Towers" }, ...towers.map((t) => ({ value: t, label: t }))] },
    { key: "dueStatus", label: "Due Status", type: "select", options: [{ value: "all", label: "All" }, { value: "overdue", label: "Overdue" }, { value: "upcoming", label: "Upcoming" }] },
    { key: "dueDateFrom", label: "Due From", type: "date" },
    { key: "dueDateTo", label: "Due To", type: "date" },
    { key: "amountMin", label: "Min Amount", type: "amount", placeholder: "₹ Min" },
    { key: "amountMax", label: "Max Amount", type: "amount", placeholder: "₹ Max" },
  ], [societies, towers]);

  const filtered = useMemo(() => {
    return unpaidBills.filter((b) => {
      if (filters.society !== "all" && b.society !== filters.society) return false;
      if (filters.tower !== "all" && b.towerNo !== filters.tower) return false;
      if (filters.dueStatus === "overdue" && !b.isOverdue) return false;
      if (filters.dueStatus === "upcoming" && b.isOverdue) return false;
      if (filters.dueDateFrom && b.dueDate < filters.dueDateFrom) return false;
      if (filters.dueDateTo && b.dueDate > filters.dueDateTo) return false;
      if (filters.amountMin && (b.netPayable || 0) < parseFloat(filters.amountMin)) return false;
      if (filters.amountMax && (b.netPayable || 0) > parseFloat(filters.amountMax)) return false;
      return true;
    });
  }, [unpaidBills, filters]);

  const totalDue = filtered.reduce((s, b) => s + (b.netPayable || 0), 0);

  const toggleId = (id) => setSelectedIds((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = (checked) => { if (checked) setSelectedIds(new Set(filtered.map((b) => b.id))); else setSelectedIds(new Set()); };
  const allSelected = filtered.length > 0 && filtered.every((b) => selectedIds.has(b.id));

  const handleMarkPaid = (bill) => setPayBill(bill);
  const handlePaid = () => { getBills().then(setBills); setPayBill(null); };

  const handleBulkPay = async () => {
    const ids = Array.from(selectedIds);
    await bulkMarkPaid(ids, bulkPayDate, bulkPayMode);
    setBills(await getBills());
    setSelectedIds(new Set());
    setBulkPayOpen(false);
    toast.success(`${ids.length} bill${ids.length !== 1 ? "s" : ""} marked as paid`);
  };

  const handleExport = () => exportCSV(filtered, [
    { label: "Customer", accessor: (r) => r.customerName },
    { label: "Invoice No", accessor: (r) => r.invoiceNo },
    { label: "Society", accessor: (r) => r.society },
    { label: "Due Date", accessor: (r) => new Date(r.dueDate).toLocaleDateString("en-IN") },
    { label: "Amount", accessor: (r) => (r.netPayable || 0).toFixed(2) },
    { label: "Days Overdue", accessor: (r) => r.daysOverdue },
  ], "dues_tracker");

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1600px] mx-auto" data-testid="dues-tracker-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-black text-zinc-900">Dues Tracker</h1>
        <div className="flex gap-2">
          {selectedIds.size > 0 && (
            <Button data-testid="bulk-pay-button" onClick={() => setBulkPayOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs">
              <CheckCircle className="w-3 h-3 mr-1" /> Pay {selectedIds.size} Selected
            </Button>
          )}
          <Button data-testid="export-dues-csv" variant="outline" size="sm" onClick={handleExport} className="rounded-sm text-xs">
            <FileDown className="w-3 h-3 mr-1" /> Export CSV
          </Button>
        </div>
      </div>
      <p className="text-sm text-zinc-500 mb-6">
        {filtered.filter((b) => b.isOverdue).length} overdue &middot; Total Due: <span className="font-semibold text-red-600 font-mono">₹{totalDue.toFixed(2)}</span>
      </p>

      <FilterBar
        pageKey="dues"
        defaults={DUES_FILTER_DEFAULTS}
        config={filterConfig}
        filters={filters}
        setFilters={setFilters}
        filteredCount={filtered.length}
        totalCount={unpaidBills.length}
      />

      {filtered.length === 0 ? (
        <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none">
          <CardContent className="py-16 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <p className="text-lg font-semibold text-zinc-700">All clear! No pending dues.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50">
                    <TableHead className="w-10"><Checkbox data-testid="select-all-dues" checked={allSelected} onCheckedChange={toggleAll} /></TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Customer</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Invoice</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Society</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Due Date</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 text-right">Amount</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Status</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 text-right sticky right-0 bg-zinc-50">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((bill) => (
                    <TableRow key={bill.id} data-testid={`due-bill-${bill.id}`} className={`hover:bg-zinc-50 ${selectedIds.has(bill.id) ? "bg-orange-50/50" : ""}`}>
                      <TableCell><Checkbox checked={selectedIds.has(bill.id)} onCheckedChange={() => toggleId(bill.id)} /></TableCell>
                      <TableCell className="text-sm font-medium">{bill.customerName}</TableCell>
                      <TableCell className="font-mono text-sm">{bill.invoiceNo}</TableCell>
                      <TableCell className="text-sm">{bill.society}</TableCell>
                      <TableCell className="text-sm">{new Date(bill.dueDate).toLocaleDateString("en-IN")}</TableCell>
                      <TableCell className="font-mono text-sm font-semibold text-right text-red-600">
                        ₹{(bill.remaining || bill.netPayable || 0).toFixed(2)}
                        {bill.displayStatus === "partial" && <span className="block text-[9px] text-zinc-400 font-normal">of ₹{(bill.netPayable || 0).toFixed(2)}</span>}
                      </TableCell>
                      <TableCell>
                        {bill.displayStatus === "partial" ? (
                          <Badge className="bg-amber-100 text-amber-700 text-[10px]">Partial</Badge>
                        ) : bill.isOverdue ? (
                          <Badge variant="destructive" className="text-[10px]"><AlertTriangle className="w-3 h-3 mr-1" />{bill.daysOverdue}d overdue</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px]">Unpaid</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right sticky right-0 bg-white">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/bills/invoice/${bill.id}`)}><Eye className="w-4 h-4" /></Button>
                          <Button size="sm" onClick={() => handleMarkPaid(bill)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs">
                            <CheckCircle className="w-3 h-3 mr-1" /> Pay
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <PaymentDialog bill={payBill} open={!!payBill} onOpenChange={(open) => { if (!open) setPayBill(null); }} onPaid={handlePaid} />

      {/* Bulk Pay Dialog */}
      <Dialog open={bulkPayOpen} onOpenChange={setBulkPayOpen}>
        <DialogContent className="max-w-sm rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Bulk Mark as Paid</DialogTitle>
            <DialogDescription>{selectedIds.size} bill{selectedIds.size !== 1 ? "s" : ""} selected</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Payment Date</Label>
              <Input data-testid="bulk-pay-date" type="date" value={bulkPayDate} onChange={(e) => setBulkPayDate(e.target.value)} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
            </div>
            <div>
              <Label className="text-xs">Payment Mode</Label>
              <Select value={bulkPayMode} onValueChange={setBulkPayMode}>
                <SelectTrigger className="bg-zinc-50 border-zinc-300 rounded-none mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Cash", "UPI", "Cheque", "Bank Transfer", "Card"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="confirm-bulk-pay" onClick={handleBulkPay} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">
              Mark {selectedIds.size} as Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
